import React from "react";
import { ArrowLeft } from "lucide-react";
import { LEGAL_PAGES } from "./legal";

const GOLD = "#E2DD9F";

/**
 * LegalPage — public-facing static page for privacy/terms/refund/support.
 * Accessible without auth via #/privacy, #/terms, #/refund, #/support.
 *
 * Props:
 *   id: "privacy" | "terms" | "refund" | "support"
 */
export function LegalPage({ id = "privacy" }) {
  const page = LEGAL_PAGES[id] || LEGAL_PAGES.privacy;

  const handleBack = () => {
    if (window.history.length > 1) {
      window.history.back();
    } else {
      window.location.hash = "#/";
    }
  };

  return (
    <div
      style={{
        minHeight: "100vh",
        background: "#0A0A0A",
        color: "#e8e8e8",
        fontFamily: "'DM Sans', sans-serif",
        padding: "40px 24px 80px",
      }}
    >
      {/* Ambient glow */}
      <div
        aria-hidden="true"
        style={{
          position: "fixed",
          inset: 0,
          background: `radial-gradient(ellipse 800px 500px at 50% 0%, rgba(226,221,159,0.06), transparent 70%)`,
          pointerEvents: "none",
        }}
      />

      <div style={{ position: "relative", maxWidth: 780, margin: "0 auto" }}>
        {/* Back button */}
        <button
          onClick={handleBack}
          className="btn"
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 6,
            padding: "8px 14px",
            background: "rgba(255,255,255,0.04)",
            border: "1px solid rgba(255,255,255,0.08)",
            borderRadius: 8,
            color: "#aaa",
            fontSize: 12,
            fontWeight: 600,
            cursor: "pointer",
            fontFamily: "inherit",
            marginBottom: 32,
          }}
        >
          <ArrowLeft size={13} /> Back
        </button>

        {/* Title */}
        <div
          style={{
            fontSize: 10,
            fontWeight: 800,
            color: GOLD,
            letterSpacing: 1.8,
            textTransform: "uppercase",
            marginBottom: 8,
          }}
        >
          Legal
        </div>
        <h1
          style={{
            fontFamily: "'Bebas Neue', sans-serif",
            fontSize: 52,
            letterSpacing: 2.5,
            margin: "0 0 10px",
            lineHeight: 1,
            color: "#fff",
            textTransform: "uppercase",
          }}
        >
          {page.title}
        </h1>
        {page.subtitle && (
          <div style={{ fontSize: 13, color: "#777", marginBottom: 36 }}>
            {page.subtitle}
          </div>
        )}

        {/* Sections */}
        <div style={{ fontSize: 15, lineHeight: 1.75, color: "#c8c8c8" }}>
          {page.sections.map((s, i) => (
            <section key={i} style={{ marginBottom: 28 }}>
              {s.heading && (
                <h2
                  style={{
                    fontFamily: "'Bebas Neue', sans-serif",
                    fontSize: 22,
                    letterSpacing: 1.5,
                    color: "#fff",
                    marginTop: 20,
                    marginBottom: 12,
                    textTransform: "uppercase",
                  }}
                >
                  {s.heading}
                </h2>
              )}
              <RichText text={s.body} />
            </section>
          ))}
        </div>

        {/* Footer links */}
        <div
          style={{
            marginTop: 60,
            paddingTop: 24,
            borderTop: "1px solid rgba(255,255,255,0.08)",
            display: "flex",
            flexWrap: "wrap",
            gap: 20,
            fontSize: 12,
          }}
        >
          <LegalNavLink currentId={id} id="privacy" label="Privacy Policy" />
          <LegalNavLink currentId={id} id="terms" label="Terms of Service" />
          <LegalNavLink currentId={id} id="refund" label="Refund Policy" />
          <LegalNavLink currentId={id} id="support" label="Help & Support" />
          <div style={{ flex: 1 }} />
          {/* Thumb-sized footer links on touch screens (2026-11-04 sweep: they were 19px tall). */}
          <style>{`@media (pointer: coarse) { .legal-tap { display: inline-block; padding-top: 12px !important; padding-bottom: 12px !important; } }`}</style>
          <a
            href="#/landing"
            style={{
              color: "#666",
              textDecoration: "none",
              borderBottom: "1px dotted #444",
            }}
            className="legal-tap"
          >
            Back to BAM Coaches
          </a>
        </div>
      </div>
    </div>
  );
}

function LegalNavLink({ currentId, id, label }) {
  const active = currentId === id;
  return (
    <a
      href={`#/${id}`}
      style={{
        color: active ? GOLD : "#aaa",
        textDecoration: "none",
        fontWeight: active ? 700 : 500,
        borderBottom: active ? `1px solid ${GOLD}` : "1px solid transparent",
        paddingBottom: 2,
        transition: "all .15s ease",
      }}
      className="legal-tap"
    >
      {label}
    </a>
  );
}

/**
 * RichText — lightweight markdown-ish renderer without a dependency.
 * Supports:
 *   - **bold**
 *   - _italic_ (underscore)
 *   - line breaks (\n → <br/>)
 *   - double line breaks (\n\n → paragraph break)
 */
function RichText({ text }) {
  if (!text) return null;
  const paragraphs = text.split(/\n\n+/);
  return (
    <>
      {paragraphs.map((para, pi) => (
        <p
          key={pi}
          style={{
            margin: "0 0 14px",
            whiteSpace: "pre-wrap",
          }}
        >
          {formatInline(para)}
        </p>
      ))}
    </>
  );
}

function formatInline(text) {
  // Minimal inline parser — handles **bold** and _italic_
  const nodes = [];
  let remaining = text;
  let key = 0;
  const regex = /(\*\*[^*]+\*\*|_[^_]+_)/g;
  let lastIndex = 0;
  let match;
  while ((match = regex.exec(text)) !== null) {
    if (match.index > lastIndex) {
      nodes.push(text.slice(lastIndex, match.index));
    }
    const token = match[0];
    if (token.startsWith("**")) {
      nodes.push(
        <strong key={`b${key++}`} style={{ color: "#fff" }}>
          {token.slice(2, -2)}
        </strong>
      );
    } else if (token.startsWith("_")) {
      nodes.push(
        <em key={`i${key++}`}>{token.slice(1, -1)}</em>
      );
    }
    lastIndex = match.index + token.length;
  }
  if (lastIndex < text.length) {
    nodes.push(text.slice(lastIndex));
  }
  // eslint-disable-next-line no-unused-vars
  const unused = remaining;
  return nodes.length > 0 ? nodes : text;
}
